import { useState, useEffect } from 'react';
import firebase from 'firebase/app';
import { message } from 'antd';

export const useAdminAnnouncements = () => {
    const [allAnnouncements, setAllAnnouncements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showArchived, setShowArchived] = useState(false);

    useEffect(() => {
        const db = firebase.firestore();
        const unsubscribe = db.collection('announcements')
            .orderBy('createdAt', 'desc')
            .onSnapshot(
                (snapshot) => {
                    const announcements = snapshot.docs.map(doc => ({
                        id: doc.id,
                        ...doc.data(),
                        createdAt: doc.data().createdAt?.toDate(),
                        updatedAt: doc.data().updatedAt?.toDate(),
                        startAt: doc.data().startAt?.toDate(),
                        endAt: doc.data().endAt?.toDate(),
                    }));
                    
                    setAllAnnouncements(announcements);
                    setLoading(false);
                },
                (error) => {
                    console.error('Error in real-time listener:', error);
                    message.error('Failed to load announcements');
                    setLoading(false);
                }
            );

        return () => unsubscribe();
    }, []);

    const getFilteredAnnouncements = () => {
        if (showArchived) {
            return allAnnouncements; // Show all including archived
        } else {
            return allAnnouncements.filter(announcement => announcement.status !== 'archived');
        }
    };

    const archiveAnnouncement = async (id) => {
        try {
            const db = firebase.firestore();
            await db.collection('announcements').doc(id).update({
                status: 'archived',
                updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
                archivedAt: firebase.firestore.FieldValue.serverTimestamp(),
                endAt: firebase.firestore.FieldValue.serverTimestamp(),
            });
            message.success('Announcement archived successfully');
        } catch (error) {
            console.error('Error archiving announcement:', error);
            message.error('Failed to archive announcement');
        }
    };

    const toggleStatus = async (id, currentStatus) => {
        try {
            const db = firebase.firestore();
            const newStatus = currentStatus === 'active' ? 'paused' : 'active';
            
            await db.collection('announcements').doc(id).update({
                status: newStatus,
                updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
                [`${newStatus}At`]: firebase.firestore.FieldValue.serverTimestamp(),
            });
            
            message.success(`Announcement ${newStatus === 'active' ? 'activated' : 'paused'} successfully`);
        } catch (error) {
            console.error('Error toggling status:', error);
            message.error('Failed to update announcement status');
        }
    };

    const saveAnnouncement = async (formData, editingAnnouncement) => {
        try {
            const db = firebase.firestore();
            
            const announcementData = {
                ...formData,
                updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
            };

            if (editingAnnouncement) {
                // Update existing announcement
                await db.collection('announcements')
                    .doc(editingAnnouncement.id)
                    .update(announcementData);
                message.success('Announcement updated successfully');
            } else {
                // Create new announcement
                await db.collection('announcements').add({
                    ...announcementData,
                    count: 0,
                    createdAt: firebase.firestore.FieldValue.serverTimestamp(),
                });
                message.success('Announcement created successfully');
            }

            return true;
        } catch (error) {
            console.error('Error saving announcement:', error);
            message.error('Failed to save announcement');
            return false;
        }
    };

    return {
        allAnnouncements,
        loading,
        showArchived,
        setShowArchived,
        getFilteredAnnouncements,
        archiveAnnouncement,
        toggleStatus,
        saveAnnouncement
    };
};